// Envelope-aware API-клієнт (canonical §5.1/§5.2): успіх `{data,meta}`,
// помилка `{error:{code,message},meta}`. httponly-cookies → `credentials:"include"`.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  // Машинно-читані подробиці помилки (envelope `error.details`), напр. перелік вузлів
  // для ROUTE_ASSIGNEE_REQUIRED. Форма залежить від коду — викликач звужує тип.
  readonly details?: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId?: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(source: unknown, ...path: string[]): string | undefined {
  let current: unknown = source;
  for (const key of path) {
    if (!isRecord(current)) return undefined;
    current = current[key];
  }
  return typeof current === "string" ? current : undefined;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export interface ApiEnvelope<T> {
  data: T;
  meta: Record<string, unknown>;
}

// ── Session-expiry координація (UC-02: тихий refresh + реактивний sign-out) ──────
// Access-cookie живе ~15 хв; refresh-cookie — довше. Замість того, щоб «випадати»
// з сесії на першому ж 401 (і зависати на екрані, доки користувач не перезавантажить
// сторінку вручну), клієнт один раз тихо смикає POST /api/auth/refresh і повторює
// початковий запит. Якщо refresh теж мертвий — сповіщаємо AuthContext, який скидає
// стан у `unauthenticated`, а ProtectedRoute реактивно веде на /login.

// Ендпоінти самого auth-циклу: їхній 401 НЕ має тригерити refresh/повтор — інакше
// рекурсія (refresh→refresh) і проковтнута помилка невірних кредів на /login.
function isAuthEndpoint(path: string): boolean {
  return (
    path.startsWith("/api/auth/login") ||
    path.startsWith("/api/auth/refresh") ||
    path.startsWith("/api/auth/logout")
  );
}

// AuthContext реєструє тут скидання сесії. Один обробник — на застосунок один
// AuthProvider (повторна реєстрація просто перезаписує попередній).
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

// Дедуплікований refresh: десятки паралельних 401 діляться ОДНИМ /refresh-запитом.
let refreshInFlight: Promise<boolean> | null = null;
async function attemptSilentRefresh(): Promise<boolean> {
  if (refreshInFlight === null) {
    refreshInFlight = fetch(`${BASE_URL}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}


// W1-6 (CAPACITY-8020): дзеркало backend FILE_MAX_SIZE_BYTES (file_object.py) —
// файл понад ліміт гарантовано відхилять, нема сенсу ганяти 100+ MiB у мережу.
const FILE_MAX_SIZE_BYTES = 100 * 1024 * 1024;

function assertFilesWithinLimit(form: FormData): void {
  for (const value of Array.from(form.values())) {
    if (typeof value !== "string" && value.size > FILE_MAX_SIZE_BYTES) {
      throw new ApiError(
        413,
        "FILE_TOO_LARGE",
        "Файл завеликий: ліміт 100 МіБ",
      );
    }
  }
}

export async function apiRequestEnvelope<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const hasBody = options.body !== undefined;
  // FormData (multipart): не серіалізуємо в JSON і не ставимо Content-Type —
  // браузер сам додасть multipart boundary.
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  if (isFormData) assertFilesWithinLimit(options.body as FormData);
  // Заголовки: кастомні з options + Content-Type для JSON-тіла (не для FormData).
  // Content-Type ставимо ОСТАННІМ — для JSON-тіла він авторитетний і не перетирається
  // кастомним заголовком; для FormData не додаємо (multipart boundary ставить браузер).
  const headers: Record<string, string> = {};
  if (options.headers) Object.assign(headers, options.headers);
  if (hasBody && !isFormData) headers["Content-Type"] = "application/json";

  const sendOnce = async (): Promise<Response> => {
    try {
      return await fetch(`${BASE_URL}${path}`, {
        method: options.method ?? "GET",
        credentials: "include",
        headers: Object.keys(headers).length > 0 ? headers : undefined,
        body: hasBody
          ? isFormData
            ? (options.body as FormData)
            : JSON.stringify(options.body)
          : undefined,
        signal: options.signal,
      });
    } catch {
      throw new ApiError(0, "NETWORK_ERROR", "Немає звʼязку з сервером");
    }
  };

  let response = await sendOnce();

  // 401 (окрім самих auth-ендпоінтів) → тихий refresh + один повтор; при повторному
  // 401 — глобальний sign-out (реактивний перехід на /login без перезавантаження).
  // Повтор безпечний навіть для POST: 401 віддає auth-middleware ДО обробника,
  // тобто запит іще не виконувався на сервері.
  if (response.status === 401 && !isAuthEndpoint(path)) {
    if (await attemptSilentRefresh()) {
      response = await sendOnce();
    }
    if (response.status === 401) {
      unauthorizedHandler?.();
    }
  }

  if (response.status === 204) {
    return { data: undefined as T, meta: {} };
  }

  let text: string;
  try {
    text = await response.text();
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Помилка читання відповіді сервера");
  }
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }
  const requestId = readString(payload, "meta", "request_id");

  if (!response.ok) {
    const code = readString(payload, "error", "code") ?? "HTTP_ERROR";
    const message =
      readString(payload, "error", "message") ?? `Помилка ${response.status}`;
    const details =
      isRecord(payload) && isRecord(payload.error) ? payload.error.details : undefined;
    throw new ApiError(response.status, code, message, requestId, details);
  }

  const meta = isRecord(payload) && isRecord(payload.meta) ? payload.meta : {};
  if (isRecord(payload) && "data" in payload) {
    return { data: payload.data as T, meta };
  }
  return { data: payload as T, meta };
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return (await apiRequestEnvelope<T>(path, options)).data;
}

// Same-origin URL для прямого посилання на ресурс (напр. `<img src>` для inline-
// перегляду, ADR-033). Auth — httponly cookie, тож same-origin GET авторизується сам.
export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

// Сирі байти (не envelope) — для клієнтського рендера файлів inline (ADR-033).
// `credentials:"include"` → httponly-cookie автентифікація; same-origin (канон CSP §8).
export async function apiFetchArrayBuffer(
  path: string,
  signal?: AbortSignal,
): Promise<ArrayBuffer> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      credentials: "include",
      signal,
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Немає звʼязку з сервером");
  }
  if (!response.ok) {
    let payload: unknown = null;
    try {
      payload = JSON.parse(await response.text());
    } catch {
      payload = null;
    }
    const code = readString(payload, "error", "code") ?? "HTTP_ERROR";
    const message =
      readString(payload, "error", "message") ?? `Помилка ${response.status}`;
    throw new ApiError(response.status, code, message);
  }
  return response.arrayBuffer();
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> =>
    apiRequest<T>(path, { signal }),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    apiRequest<T>(path, { method: "POST", body }),
};
